import { useState } from "react";
import { ChevronLeft, ChevronRight, Pencil, X } from "lucide-react";
import { Button, EmptyState, Icon, Modal, Spinner } from "@cntrl/ui";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { SettingsModal } from "../settings/SettingsModal.js";
import {
  useBandProfileView,
  bandInitials,
  memberName,
  memberInitials,
  type FriendProfileFriend,
  type FriendProfileMember,
  type FriendProfileShow,
} from "../../app/hooks/useFriends.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { CrewProfileModal } from "./CrewProfileModal.js";
import { RoleLabel } from "./RoleLabel.js";
import styles from "./BandProfileModal.module.scss";

export interface BandProfileModalProps {
  /** The band whose profile shows: the caller's own band, or an accepted friend (incl. a clickable
   * friend-of-a-friend). The hook picks the read by target — the modal never cares which door served it. */
  bandId: string;
  /** True when a Back affordance should show (the browse stack is deeper than one). */
  canGoBack?: boolean;
  onBack?: () => void;
  onClose: () => void;
  /** Open a clickable friend tile's own browse (navigates within the modal via the parent's stack). */
  onOpenFriend: (bandId: string) => void;
  /** Open a member's profile — the shared `CrewProfileModal`. The whole row goes, not just its id: for a
   *  FRIEND band there is no seat here to resolve, so the row's own name and role are the only facts. */
  onOpenMember: (member: FriendProfileMember) => void;
}

/**
 * The band profile — one modal for BOTH the caller's own band ("View my profile" for the owner: the
 * Artist IS the band — PO, 2026-08-20) and the friend browse (the "MySpace for working bands" passive
 * channel). READ-ONLY: nothing here writes, and it renders ONLY what `FriendProfileRead` carries
 * (header / members / friends / upcomingShows) — no finances/contacts/ops/merch, which the DTO
 * structurally cannot carry (never invent fields).
 *
 * The CrewProfileModal anatomy on purpose ("they're very similar to user profiles"): a dark identity
 * rail — the band's main photo as the top square's background with name/location/genres bottom-aligned,
 * the FRIENDS grid below — against a paper reading pane holding MEMBERS and UPCOMING SHOWS. Members
 * open their PublicProfileView; clickable friend tiles navigate to that band's browse; non-clickable
 * tiles render passive. A 404 (unfriended under us) renders the designed "not available" state.
 */
export function BandProfileModal({
  bandId,
  canGoBack = false,
  onBack,
  onClose,
  onOpenFriend,
  onOpenMember,
}: BandProfileModalProps) {
  const q = useBandProfileView(bandId);
  const header = q.data?.header;
  const name = header?.bandName || "Band";

  // The band's identity editor is Settings → Artist Profile (genres, bio, the main photo) — the owner
  // has no person record to edit (the Artist IS the band). The pencil shows on the caller's OWN band
  // for a band:manage seat, at the top of the browse stack only (deeper, the slot belongs to Back).
  const { bandId: activeBandId } = useBandContext();
  const canManage = useCapability("band:manage");
  const canEdit = bandId === activeBandId && canManage && !canGoBack;
  const [editing, setEditing] = useState(false);

  // The hero photo rides the ONE band-photo path — the tenant-less global avatar route
  // (`useAvatarUrl("band", …)`) — which serves reachable and unreachable bands alike, a friend band we
  // hold no seat on included. The band-scoped media route it used to fall back to 403s for any band the
  // viewer has no seat on, so a friend band's hero stayed plain ink; this is the fix the friend tiles got.
  // Photo-less still stays plain ink (no initials on the hero).
  const heroUrl = useAvatarUrl("band", bandId, header?.avatarMediaId) ?? null;

  if (q.isLoading || q.isError) {
    return (
      <Modal title={name} onClose={onClose} closeLabel="Close profile" size="xl" hideHeader bodyLayout="flush">
        <div className={styles.stateBody}>
          <button type="button" className={styles.stateClose} aria-label="Close profile" onClick={onClose}>
            <Icon icon={X} size={20} />
          </button>
          {q.isLoading ? (
            <div className={styles.center} role="status" aria-live="polite">
              <Spinner size={22} label="Loading profile…" />
            </div>
          ) : q.error instanceof ApiError && q.error.status === 404 ? (
            <EmptyState title="Profile not available" description="You're no longer friends, or this band is gone." />
          ) : (
            <EmptyState
              title="Couldn't load this profile"
              description={q.error instanceof Error ? q.error.message : "Please try again."}
              action={<Button onClick={() => void q.refetch()}>Retry</Button>}
            />
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={name} onClose={onClose} closeLabel="Close profile" size="xl" hideHeader bodyLayout="flush">
      <div className={styles.split}>
        <aside className={styles.rail}>
          {/* The rail's top square: the band's MAIN photo behind, identity bottom-aligned (the
              CrewProfileModal hero). No initials fallback — photo-less stays plain ink. */}
          <div className={heroUrl ? styles.hero : `${styles.hero} ${styles.heroBare}`}>
            <button type="button" className={styles.close} aria-label="Close profile" onClick={onClose}>
              <Icon icon={X} size={20} />
            </button>
            {canGoBack && (
              <button type="button" className={styles.back} aria-label="Back" onClick={onBack}>
                <Icon icon={ChevronLeft} size={20} />
              </button>
            )}
            {canEdit && (
              <button type="button" className={styles.back} aria-label="Edit band profile" onClick={() => setEditing(true)}>
                <Icon icon={Pencil} size={16} />
              </button>
            )}
            {heroUrl && <img className={styles.heroPhoto} src={heroUrl} alt="" data-photo="" />}
            <div className={styles.heroText}>
              <h2 className={styles.heroName}>{name}</h2>
              {header?.location && <p className={styles.location}>{header.location}</p>}
              {(header?.genres?.length ?? 0) > 0 && (
                <p className={styles.genres}>{header?.genres?.join(" · ")}</p>
              )}
            </div>
          </div>

          <div className={styles.railBody}>
            {header?.shortBio && <p className={styles.bio}>{header.shortBio}</p>}
            <FriendsSection friends={q.data?.friends ?? []} onOpenFriend={onOpenFriend} />
          </div>
        </aside>

        <section className={styles.body}>
          <MembersSection members={q.data?.members ?? []} onOpenMember={onOpenMember} />
          <ShowsSection shows={q.data?.upcomingShows ?? []} />
        </section>
      </div>

      {/* Settings → Artist Profile, stacked over the browse (the ordinary Escape stack). Closing
          refetches this profile so a changed name/genres/photo repaints immediately. */}
      {editing && (
        <SettingsModal
          initialTab="profile"
          onClose={() => {
            setEditing(false);
            void q.refetch();
          }}
        />
      )}
    </Modal>
  );
}

/**
 * The self-contained browse: owns the navigation stack (clickable friend tiles push, Back pops) and the
 * stacked member profile, so every door — FriendsCard, the owner's "View my profile", the owner
 * crew row, the crew modal's band tiles — gets identical behavior from one line.
 */
export function BandProfileBrowser({ initialBandId, onClose }: { initialBandId: string; onClose: () => void }) {
  const [stack, setStack] = useState<string[]>([initialBandId]);
  const [member, setMember] = useState<FriendProfileMember | null>(null);
  const top = stack[stack.length - 1] ?? initialBandId;

  // The band currently on screen, so a member opened from here gets ITS photo blurred behind them — a
  // friend band's member has no shared band with the viewer, so the modal cannot derive it. Cache-shared
  // with the BandProfileModal rendered below (same query key), so this costs no extra request.
  const topBand = useBandProfileView(top);

  return (
    <>
      <BandProfileModal
        bandId={top}
        canGoBack={stack.length > 1}
        onBack={() => setStack((s) => s.slice(0, -1))}
        onClose={onClose}
        onOpenFriend={(id) => setStack((s) => [...s, id])}
        onOpenMember={setMember}
      />
      {/* Stacked ABOVE the browse modal (later in the DOM). `CrewProfileModal` is THE person dialog — the
          same one the crew card and "View my profile" open — so a member reads identically whichever door
          you came through, with the Edit pencil the only difference when it is you (2026-09-01). It used to
          be `ProfileModal` in view-others mode, a 520px card with none of the identity rail. The member row
          is passed whole as the hero's HEAD START: this band may not be ours, in which case there is no seat
          to resolve and the row's own name and role are all there is. */}
      {member?.personId && (
        <CrewProfileModal
          personId={member.personId}
          fallback={{ name: memberName(member), role: member.role, isOwner: member.isOwner }}
          bandBackdrop={{ bandId: top, avatarMediaId: topBand.data?.header?.avatarMediaId ?? null }}
          onClose={() => setMember(null)}
        />
      )}
    </>
  );
}

// ---- members --------------------------------------------------------------------------------------------

export function MembersSection({
  members,
  onOpenMember,
}: {
  members: FriendProfileMember[];
  onOpenMember: (member: FriendProfileMember) => void;
}) {
  return (
    <section className={styles.section} aria-labelledby="band-members-heading">
      <p id="band-members-heading" className={styles.sectionLabel}>
        Members
      </p>
      {members.length === 0 ? (
        <p className={styles.sectionEmpty}>No members listed.</p>
      ) : (
        <ul className={styles.memberList}>
          {members.map((m) => (
            <li key={m.personId}>
              <button
                type="button"
                className={styles.memberRow}
                onClick={() => m.personId && onOpenMember(m)}
              >
                <PersonAvatar
                  personId={m.personId}
                  avatarMediaId={m.avatarMediaId}
                  initials={memberInitials(m)}
                  shape="rounded"
                />
                <span className={styles.memberName}>{memberName(m)}</span>
                {/* The owner seat reads "Artist" + the mark — the owner IS the band. */}
                {m.role && <RoleLabel role={m.role} owner={m.isOwner} className={styles.memberRole} />}
                <Icon icon={ChevronRight} size={20} className={styles.memberChevron} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- friends grid (on the rail) -------------------------------------------------------------------------

function FriendsSection({
  friends,
  onOpenFriend,
}: {
  friends: FriendProfileFriend[];
  onOpenFriend: (bandId: string) => void;
}) {
  return (
    <section className={styles.section} aria-labelledby="band-friends-heading">
      <p id="band-friends-heading" className={styles.sectionLabel}>
        Friends
      </p>
      {friends.length === 0 ? (
        <p className={styles.sectionEmpty}>No friends listed.</p>
      ) : (
        <ul className={styles.friendGrid}>
          {friends.map((f) => {
            const label = f.name || "Band";
            const photo = <FriendTilePhoto f={f} />;
            // Clickable tiles (a band within the viewer's reach) navigate to that band's browse; the rest
            // render passive — no click, no navigation (browsable but not infinitely traversable).
            if (f.clickable && f.bandId) {
              return (
                <li key={f.bandId}>
                  <button type="button" className={styles.friendTile} onClick={() => onOpenFriend(f.bandId as string)}>
                    {photo}
                    <span className={styles.friendTileName}>{label}</span>
                  </button>
                </li>
              );
            }
            return (
              <li key={f.bandId ?? label}>
                <div className={`${styles.friendTile} ${styles.friendTilePassive}`} aria-disabled="true">
                  {photo}
                  <span className={styles.friendTileName}>{label}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** A friend tile's band picture — the global avatar route (the one band-photo path), initials fallback. Its
 *  own component so each tile in the grid can host the resolution hook. */
function FriendTilePhoto({ f }: { f: FriendProfileFriend }) {
  const url = useAvatarUrl("band", f.bandId, f.avatarMediaId);
  return url ? (
    <img className={styles.friendPhoto} src={url} alt="" data-photo="" />
  ) : (
    <span className={styles.friendPhoto} aria-hidden="true">
      {bandInitials(f.name)}
    </span>
  );
}

// ---- upcoming shows -------------------------------------------------------------------------------------

/** Month abbreviations for the wall-clock show date (presentation only — the client never tz-converts). */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Split a YYYY-MM-DD wall-clock date into the stacked date block, without any timezone shift. */
function showDateParts(date: string | undefined): { month: string; day: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date ?? "");
  if (!m) return { month: "", day: "" };
  return { month: MONTHS[Number(m[2]) - 1] ?? m[2], day: String(Number(m[3])).padStart(2, "0") };
}

export function ShowsSection({ shows }: { shows: FriendProfileShow[] }) {
  return (
    <section className={styles.section} aria-labelledby="band-shows-heading">
      <p id="band-shows-heading" className={styles.sectionLabel}>
        Upcoming shows
      </p>
      {shows.length === 0 ? (
        <p className={styles.sectionEmpty}>No upcoming shows.</p>
      ) : (
        <ul className={styles.showList}>
          {shows.map((s) => {
            const d = showDateParts(s.date);
            return (
              <li key={s.showId} className={styles.showRow}>
                <span className={styles.showDate} aria-hidden="true">
                  <span className={styles.showMonth}>{d.month}</span>
                  <span className={styles.showDay}>{d.day}</span>
                </span>
                <span className={styles.showMain}>
                  <span className={styles.showVenue}>{s.venue || "Venue TBA"}</span>
                  {s.city && <span className={styles.showCity}>{s.city}</span>}
                </span>
                {s.kind === "festival" && <span className={styles.festivalTag}>Festival</span>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
